<ion-app>

  <!-- ══════════════════════════════════════════════════════════════
       CUSTOM OVERLAY DRAWER — matches mobile-driver style exactly
  ══════════════════════════════════════════════════════════════ -->
  @if (drawerOpen()) {
    <div class="fixed inset-0 z-50 flex">

      <!-- Backdrop -->
      <div class="fixed inset-0 bg-black/40 backdrop-blur-sm"
           (click)="drawerService.close()"></div>

      <!-- Drawer panel -->
      <aside class="relative z-10 w-72 max-w-[85vw] h-full shadow-2xl flex flex-col drawer-panel"
             style="background: #32737E;">

        <!-- Profile / logo header -->
        <div class="px-6 flex flex-col items-center border-b border-white/10"
             style="padding-top: calc(2.5rem + env(safe-area-inset-top, 0px)); padding-bottom: 2rem;">

          <img src="assets/icon/app-icon.png" alt="syncc Pasajeros" class="h-16 w-16 rounded-2xl shadow-lg mb-8"
               onerror="this.style.display='none'">

          @if (isLoggedIn()) {
            <!-- Logged-in: avatar + name -->
            <div class="w-20 h-20 rounded-full bg-white flex items-center justify-center
                        text-[#32737E] text-3xl font-bold mb-4 shadow-lg">
              {{ currentUser()!.name?.charAt(0)?.toUpperCase() ?? '?' }}
            </div>
            <div class="text-center">
              <h2 class="text-white font-bold text-xl leading-tight">
                {{ currentUser()!.name ?? 'Pasajero' }}
              </h2>
              <p class="text-white/60 text-xs font-medium mt-1 uppercase tracking-widest">Pasajero</p>
            </div>
          } @else {
            <!-- Guest: wordmark — "Sale" in Outfit, the splash's display face -->
            <div class="text-center">
              <h2 class="text-white text-3xl tracking-tight leading-none"
                  style="font-family: Outfit, Inter, system-ui, -apple-system, 'Segoe UI', sans-serif; font-weight: 600;">
                Sale
              </h2>
            </div>
          }
        </div>

        <!-- Nav links -->
        <nav class="flex-1 py-4 overflow-y-auto px-3">
          @for (item of menuItems; track item.route) {
            <button
              (click)="navigate(item.route)"
              class="w-full flex items-center gap-3 px-4 py-3.5 rounded-xl font-semibold transition-all text-sm text-white mb-0.5"
              [style.background]="isActive(item.route) ? 'rgba(255,255,255,0.15)' : 'transparent'"
              [style.opacity]="isActive(item.route) ? '1' : '0.7'"
            >
              <svg class="w-5 h-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      [attr.d]="item.svgPath" />
              </svg>
              {{ item.label }}

              <!-- Favorites badge -->
              @if (item.route === '/favorites' && favoriteCount() > 0) {
                <span class="ml-auto text-[10px] font-bold px-2 py-0.5 rounded-full"
                      style="background: rgba(255,255,255,0.2);">
                  {{ favoriteCount() }}
                </span>
              }
            </button>
          }

          <!-- Mi Perfil -->
          <button
            (click)="navigate('/profile')"
            class="w-full flex items-center gap-3 px-4 py-3.5 rounded-xl font-semibold transition-all text-sm text-white mb-0.5"
            [style.background]="isActive('/profile') ? 'rgba(255,255,255,0.15)' : 'transparent'"
            [style.opacity]="isActive('/profile') ? '1' : '0.7'"
          >
            <svg class="w-5 h-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
            Mi Perfil
          </button>

          <!-- Verificar Permisos -->
          <button
            (click)="checkPermissions()"
            class="w-full flex items-center gap-3 px-4 py-3.5 rounded-xl font-semibold transition-all text-sm text-white mb-0.5"
            style="opacity: 0.7;"
          >
            <svg class="w-5 h-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </svg>
            Verificar Permisos
          </button>
        </nav>

        <!-- Divider -->
        <div class="mx-6 border-t border-white/10"></div>

        <!-- Bottom section -->
        <div class="px-6 py-6">
          @if (isLoggedIn()) {
            <button (click)="logout()"
              class="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-white font-bold transition-all text-sm"
              style="opacity: 0.8;"
              onmouseenter="this.style.opacity='1';this.style.background='rgba(255,255,255,0.1)'"
              onmouseleave="this.style.opacity='0.8';this.style.background='transparent'">
              <svg class="w-5 h-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              Cerrar Sesión
            </button>
          } @else {
            <div class="flex flex-col gap-2">
              <!-- Primary: Iniciar Sesión -->
              <button (click)="navigate('/login', { mode: 'login' })"
                class="w-full flex items-center justify-center gap-2.5 px-4 py-3 rounded-xl font-bold text-sm transition-all active:scale-[0.98]"
                style="background: #fff; color: #32737E;">
                <svg class="w-5 h-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                </svg>
                Iniciar Sesión
              </button>

              <!-- Secondary: Crear Cuenta -->
              <button (click)="navigate('/login', { mode: 'register' })"
                class="w-full flex items-center justify-center gap-2.5 px-4 py-3 rounded-xl font-bold text-sm text-white transition-all active:scale-[0.98]"
                style="background: rgba(255,255,255,0.15); border: 1px solid rgba(255,255,255,0.25);">
                <svg class="w-5 h-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                </svg>
                Crear una Cuenta
              </button>
            </div>
          }
        </div>

        <!-- Version -->
        <p class="text-white/25 text-[10px] text-center pb-6 font-medium">
          Sale v{{ appVersion }}
        </p>

      </aside>
    </div>
  }

  <!-- Main content outlet -->
  <ion-router-outlet></ion-router-outlet>

  <!-- NOT LAUNCHING YET — restore: change @if (false) back to @if (activeBoarding()) -->
  @if (false) { <!-- QR/Payment: "En el bus" boarding banner — original: activeBoarding() -->
    <div class="fixed left-0 right-0 z-40 px-3"
         style="bottom: calc(6.5rem + env(safe-area-inset-bottom, 0px));">
      <button (click)="openQrModal('show')"
              class="w-full flex items-center gap-3 rounded-2xl px-4 py-3 shadow-xl active:scale-[0.98] transition-transform"
              style="background: #1e293b; border: 1.5px solid rgba(255,255,255,0.08);">

        <!-- Bus icon + info -->
        <div class="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
             style="background: #32737E;">
          <svg class="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M8 7h8M8 11h5m-9 8h14a2 2 0 002-2V7a2 2 0 00-2-2H4a2 2 0 00-2 2v10a2 2 0 002 2z"/>
          </svg>
        </div>

        <div class="flex-1 min-w-0 text-left">
          <p class="text-white text-xs font-bold truncate leading-tight">{{ activeBoarding()!.lineName }}</p>
          <p class="text-[10px] leading-tight" style="color: #64748b;">En el bus · {{ boardingElapsed() }}</p>
        </div>

        <!-- CTA -->
        <div class="flex-shrink-0 flex flex-col items-center rounded-xl px-3 py-1.5"
             style="background: rgba(255,255,255,0.1); border: 1px solid rgba(255,255,255,0.15);">
          <span class="text-white text-[11px] font-bold leading-tight whitespace-nowrap">Mostrar QR</span>
          <span class="text-[9px] font-medium leading-tight whitespace-nowrap" style="color: #34d399;">para bajar</span>
        </div>

      </button>
    </div>
  }

  <!-- NOT LAUNCHING YET — restore: change @if (false) back to @if (isLoggedIn() && !overlayOpen()) -->
  @if (false) { <!-- QR/Payment: QR floating action button — original: isLoggedIn() && !overlayOpen() -->
    <button (click)="openQrModal('scan')"
      class="fixed left-1/2 -translate-x-1/2 z-40 w-16 h-16 rounded-full shadow-2xl flex items-center justify-center active:scale-90 transition-transform"
      style="bottom: calc(2rem + env(safe-area-inset-bottom, 0px)); background: #32737E; border: 3px solid white;">
      <!-- Proper QR code icon -->
      <svg class="w-7 h-7 text-white" viewBox="0 0 24 24" fill="currentColor">
        <path fill-rule="evenodd" clip-rule="evenodd"
          d="M3 3h7v7H3V3zm1.5 1.5v4h4v-4h-4zm9-1.5h7v7h-7V3zm1.5 1.5v4h4v-4h-4zM3 14h7v7H3v-7zm1.5 1.5v4h4v-4h-4zm9.5-1h1.5v1.5H14V14zm3 0h1.5v1.5H17V14zm-3 3h1.5v1.5H14V17zm3-1.5h1.5v1.5H17v-1.5zm1.5 1.5H20v1.5h-1.5V17zm-4.5 1.5h1.5V20H14v-1.5zm3 0h1.5V20H17v-1.5zm1.5-4.5H20V14h-1.5v1.5z"/>
      </svg>
    </button>
  }

  <!-- NOT LAUNCHING YET — restore: change @if (false) back to @if (qrModalOpen()) -->
  @if (false) { <!-- QR/Payment: QR modal — original: qrModalOpen() -->
    <div class="fixed inset-0 z-[200] flex flex-col bg-white">

      <!-- Header — teal navbar, matches app style -->
      <div class="flex items-center justify-between px-5 pb-4 shrink-0"
        style="background: #32737E; padding-top: calc(3.5rem + env(safe-area-inset-top, 0px));">
        <div>
          <p class="text-[10px] font-bold uppercase tracking-widest" style="color: rgba(255,255,255,0.55);">Sale</p>
          <h2 class="text-base font-black text-white mt-0.5">Mi QR</h2>
        </div>
        <button (click)="closeQrModal()"
          class="w-10 h-10 rounded-full flex items-center justify-center active:scale-90 transition-transform"
          style="background: rgba(255,255,255,0.18);">
          <svg class="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M6 18L18 6M6 6l12 12"/>
          </svg>
        </button>
      </div>

      <!-- Tab switcher — white bg, teal active pill -->
      <div class="px-5 py-4 bg-white border-b border-slate-100 shrink-0">
        <div class="flex rounded-2xl p-1 bg-slate-100">
          <button (click)="switchQrTab('scan')"
            class="flex-1 py-2.5 rounded-xl text-sm font-bold transition-all"
            [style.background]="qrTab() === 'scan' ? '#32737E' : 'transparent'"
            [style.color]="qrTab() === 'scan' ? '#ffffff' : '#64748b'">
            Escanear QR
          </button>
          <button (click)="switchQrTab('show')"
            class="flex-1 py-2.5 rounded-xl text-sm font-bold transition-all"
            [style.background]="qrTab() === 'show' ? '#32737E' : 'transparent'"
            [style.color]="qrTab() === 'show' ? '#ffffff' : '#64748b'">
            Mostrar QR
          </button>
        </div>
      </div>

      <!-- Content — light gray background, min-h-0 lets flex-1 shrink and scroll -->
      <div class="flex-1 min-h-0 flex flex-col items-center justify-start pt-6 px-6 pb-8 gap-6 overflow-y-auto bg-slate-50">

        @if (qrTab() === 'scan') {
          @if (scanState() === 'scanning') {
            <!-- Scanner wrapper — sized by the library, no fixed height to avoid gaps -->
            <div class="w-full max-w-xs rounded-2xl overflow-hidden shadow-lg border border-slate-200 qr-scanner-wrap">
              <div id="qr-modal-scanner-region" class="w-full"></div>
            </div>
            <p class="text-slate-500 text-sm text-center font-medium">Apunta al código QR del conductor</p>
          }
          @if (scanState() === 'success') {
            <div class="w-20 h-20 rounded-full flex items-center justify-center bg-emerald-50">
              <svg class="w-10 h-10 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7"/>
              </svg>
            </div>
            <div class="text-center">
              <h2 class="text-slate-800 font-black text-xl">¡Check-in exitoso!</h2>
              <p class="text-emerald-600 font-semibold text-sm mt-1">{{ scanMsg() }}</p>
            </div>
            <button (click)="retryScanner()"
              class="w-full max-w-xs py-4 rounded-2xl font-bold text-sm text-white active:scale-[0.98] transition shadow-sm"
              style="background: #32737E;">Escanear otro</button>
          }
          @if (scanState() === 'duplicate') {
            <div class="w-20 h-20 rounded-full bg-amber-50 flex items-center justify-center">
              <svg class="w-10 h-10 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
              </svg>
            </div>
            <div class="text-center">
              <p class="text-slate-800 font-black text-lg">Ya registrado</p>
              <p class="text-slate-500 text-sm mt-1">{{ scanMsg() }}</p>
            </div>
            <button (click)="retryScanner()"
              class="w-full max-w-xs py-4 rounded-2xl font-bold text-sm text-white active:scale-[0.98] transition shadow-sm"
              style="background: #32737E;">Escanear otro</button>
          }
          @if (scanState() === 'error') {
            <div class="w-20 h-20 rounded-full bg-red-50 flex items-center justify-center">
              <svg class="w-10 h-10 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </div>
            <div class="text-center">
              <p class="text-slate-800 font-black text-lg">Algo salió mal</p>
              <p class="text-slate-500 text-sm mt-1">{{ scanMsg() }}</p>
            </div>
            <button (click)="retryScanner()"
              class="w-full max-w-xs py-4 rounded-2xl font-bold text-sm text-white active:scale-[0.98] transition shadow-sm"
              style="background: #32737E;">Intentar de nuevo</button>
          }
        }

        @if (qrTab() === 'show') {
          @if (qrLoading()) {
            <div class="flex flex-col items-center gap-3">
              <div class="w-10 h-10 border-2 border-t-transparent rounded-full animate-spin"
                style="border-color: #32737E; border-top-color: transparent;"></div>
              <p class="text-sm text-slate-400 font-medium">Generando tu código...</p>
            </div>
          }
          @if (!qrLoading() && qrError()) {
            <div class="bg-red-50 border border-red-100 rounded-2xl p-6 text-center max-w-xs">
              <p class="text-sm font-semibold text-red-600">{{ qrError() }}</p>
            </div>
          }
          @if (!qrLoading() && qrDataUrl()) {
            <!-- QR Card -->
            <div class="bg-white rounded-3xl shadow-xl border border-slate-100 overflow-hidden w-full max-w-xs">
              <div class="h-2 w-full" style="background: #32737E;"></div>
              <div class="flex flex-col items-center pt-5 pb-4 px-6 border-b border-slate-100">
                <div class="w-12 h-12 rounded-full flex items-center justify-center text-white text-xl font-bold mb-2"
                  style="background: #32737E;">
                  {{ currentUser()?.name?.charAt(0)?.toUpperCase() ?? '?' }}
                </div>
                <p class="text-base font-bold text-slate-800">{{ currentUser()?.name ?? 'Pasajero' }}</p>
                <p class="text-xs text-slate-400 font-medium mt-0.5 uppercase tracking-widest">Pasajero syncc</p>
              </div>
              <div class="flex justify-center items-center p-6">
                <img [src]="qrDataUrl()" alt="Mi QR de pasajero"
                  class="w-48 h-48 rounded-xl" draggable="false"
                  style="-webkit-user-select: none; user-select: none;" />
              </div>
              <div class="px-6 pb-5 text-center">
                <p class="text-[11px] text-slate-400 leading-relaxed">Muéstralo al conductor para registrar tu viaje</p>
              </div>
            </div>
          }
        }

        <!-- ── Pagar con saldo toggle — visible in both tabs ─────── -->
        <div class="w-full max-w-xs bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">

          <!-- Toggle row -->
          <div class="flex items-center justify-between px-4 py-3.5">
            <div class="flex items-center gap-3">
              <div class="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                   style="background: #E8F2F3;">
                <svg class="w-5 h-5" style="color: #32737E;" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"/>
                </svg>
              </div>
              <div>
                <p class="text-sm font-bold text-slate-800">Pagar con saldo</p>
                <p class="text-xs text-slate-400 mt-0.5">Se descuenta al registrar el viaje</p>
                <p class="text-xs font-semibold mt-0.5" style="color: #32737E;">
                  Saldo disponible: Bs. {{ userBalance().toFixed(2) }}
                </p>
              </div>
            </div>
            <button
              (click)="onPayToggle(!paymentIntent.enabled())"
              class="relative w-12 h-6 rounded-full transition-colors duration-200 focus:outline-none flex-shrink-0"
              [style.background]="paymentIntent.enabled() ? '#32737E' : '#cbd5e1'">
              <span class="absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-200"
                    [class.translate-x-6]="paymentIntent.enabled()">
              </span>
            </button>
          </div>

          <!-- Amount input — only when toggle is ON -->
          @if (paymentIntent.enabled()) {
            <div class="px-4 pb-4 border-t border-slate-50">
              <p class="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2 mt-3">
                Monto a pagar
              </p>
              <div class="flex items-center gap-2">
                <span class="text-sm font-bold text-slate-500">Bs.</span>
                <input
                  type="text"
                  inputmode="decimal"
                  placeholder="0.00"
                  [value]="paymentIntent.amount() || ''"
                  (input)="onPayAmountChange($any($event.target).value)"
                  [style.border-color]="payAmountExceedsBalance() ? '#ef4444' : ''"
                  class="flex-1 text-lg font-bold border border-slate-200 rounded-xl px-3 py-2.5
                         bg-slate-50 focus:bg-white outline-none transition-all" />
              </div>
              @if (payAmountExceedsBalance()) {
                <p class="text-xs text-red-500 font-semibold mt-2 flex items-center gap-1">
                  <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M6 18L18 6M6 6l12 12"/>
                  </svg>
                  Saldo insuficiente · Disponible: Bs. {{ userBalance().toFixed(2) }}
                </p>
              } @else if (paymentIntent.amount() > 0) {
                <p class="text-xs text-emerald-600 font-semibold mt-2 flex items-center gap-1">
                  <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7"/>
                  </svg>
                  Listo · Bs. {{ paymentIntent.amount().toFixed(2) }} al escanear
                </p>
              } @else {
                <p class="text-xs text-amber-500 font-medium mt-2">Ingresa el monto del pasaje</p>
              }
            </div>
          }
        </div>

      </div>

      <div class="bg-slate-50 shrink-0" style="height: calc(1.5rem + env(safe-area-inset-bottom, 0px));"></div>
    </div>
  }

  @if (cityService.needsCityPicker()) {
  <div class="fixed inset-0 z-[99999] flex flex-col items-center justify-center px-8 overflow-hidden" style="background:#fff;">
    <ngx-particles
      id="city-picker-particles"
      class="absolute inset-0 w-full h-full"
      style="z-index:0"
      [options]="particlesOptions"
      [particlesInit]="particlesInit">
    </ngx-particles>
    <div class="relative z-10 mb-10 text-center">
      <img src="assets/icon/logo-syncc.svg" class="h-20 w-auto mx-auto mb-6" alt="syncc">
      <h1 class="text-2xl font-bold text-slate-900">¿En qué ciudad estás?</h1>
      <p class="mt-2 text-sm text-slate-500">No pudimos detectar tu ubicación o estás fuera de las ciudades disponibles.<br>Elige una ciudad para continuar.</p>
    </div>
    <div class="relative z-10 w-full max-w-xs flex flex-col gap-3">
      @for (city of cityService.availableCities(); track city.id) {
      <button (click)="cityService.selectCity(city)"
        class="w-full py-4 rounded-2xl bg-primary text-white font-bold text-base shadow-sm active:opacity-80 transition">
        {{ city.name }}
      </button>
      }
    </div>
  </div>
  }

</ion-app>

@if (showSplash()) {
  <sale-splash
    app="pasajeros"
    [ready]="appReady()"
    (finished)="onSplashFinished()">
  </sale-splash>
}

<style>
  .drawer-panel {
    animation: slideInLeft 0.22s ease-out;
  }

  @keyframes slideInLeft {
    from { transform: translateX(-100%); opacity: 0; }
    to   { transform: translateX(0);     opacity: 1; }
  }
</style>
